"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Spinner,
  Center,
  Image,
  Link as ChakraLink,
  Icon,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { FiSearch, FiDownload, FiEye } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import type { Document } from "../types/types";
import FilenameDialog from "./FilenameDialog";

export default function DocumentHistory() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [downloadData, setDownloadData] = useState<{
    blob: Blob;
    defaultFilename: string;
  } | null>(null);

  const portalTarget =
    typeof window !== "undefined" ? document.body : undefined;

  type StatusOption = { value: string; label: string };

  const statusOptions: StatusOption[] = [
    { value: "PROCESSING", label: "Processing" },
    { value: "COMPLETED", label: "Completed" },
    { value: "FAILED", label: "Failed" },
  ];

  const selectedStatus =
    statusOptions.find((o) => o.value === statusFilter) ?? null;

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm, statusFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not found");
      }

      const { data, error: documentsError } = await supabase
        .from("documents")
        .select(
          `
          *,
          document_type:document_type_id(name, description)
        `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (documentsError) {
        throw documentsError;
      }

      setDocuments(data || []);
    } catch (error: any) {
      console.error("Error fetching documents:", error.message);
      setError("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;

    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.document_type?.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doc.custom_document_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          doc.prompt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((doc) => doc.status === statusFilter);
    }

    setFilteredDocuments(filtered);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PROCESSING":
        return "yellow";
      case "COMPLETED":
        return "green";
      case "FAILED":
        return "red";
      default:
        return "gray";
    }
  };

  const getDocumentDisplayName = (doc: Document) => {
    // For Miscellaneous documents, use custom_document_name if available
    if (
      doc.document_type?.name === "Miscellaneous" &&
      doc.custom_document_name
    ) {
      return doc.custom_document_name;
    }
    return doc.document_type?.name || "Document";
  };

  const downloadDocument = async (doc: Document) => {
    if (!doc.output_path) {
      toast({
        title: "Download Not Available",
        description: "This document is not ready for download yet.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from("document-outputs")
        .download(doc.output_path);

      if (error) {
        throw error;
      }

      const defaultFilename = `${getDocumentDisplayName(doc)}-${
        doc.id
      }.${doc.output_format.toLowerCase()}`;

      setDownloadData({
        blob: data,
        defaultFilename,
      });
      onOpen();
    } catch (error: any) {
      console.error("Error downloading document:", error.message);
      toast({
        title: "Download Failed",
        description: "Failed to download the document. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmDownload = (filename: string) => {
    if (!downloadData) return;

    const url = URL.createObjectURL(downloadData.blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Clean up
    setDownloadData(null);

    toast({
      title: "Download Started",
      description: "Your document is being downloaded.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>

            <HStack spacing={6}>
              <ChakraLink
                as={Link}
                to="/dashboard"
                color="gray.600"
                fontWeight="medium"
              >
                Dashboard
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/document-history"
                color="primary.600"
                fontWeight="medium"
              >
                Documents
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/templates"
                color="gray.600"
                fontWeight="medium"
              >
                Templates
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/billing"
                color="gray.600"
                fontWeight="medium"
              >
                Billing
              </ChakraLink>
            </HStack>

            <Button as={Link} to="/submit-document" colorScheme="primary">
              New Document
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Page Header */}
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="xl" color="gray.800">
                Document History
              </Heading>
              <Text color="gray.600">
                View and manage all your generated documents
              </Text>
            </VStack>
          </Flex>

          {/* Filters */}
          <Flex gap={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement>
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
              />
            </InputGroup>
            <Select<StatusOption, false>
              placeholder="Filter by status"
              options={statusOptions}
              value={selectedStatus}
              onChange={(opt) => setStatusFilter(opt?.value ?? "")}
              // make the menu overflow the small window
              menuPortalTarget={portalTarget as HTMLElement}
              menuPosition="fixed"
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
              chakraStyles={{
                control: (base) => ({
                  ...base,
                  maxW: "200px",
                  bg: "white",
                  borderColor: "primary.200",
                  _hover: { borderColor: "primary.300" },
                  _focusWithin: {
                    borderColor: "primary.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                  },
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: "primary.500",
                }),
                menu: (base) => ({ ...base, zIndex: 2000, bg: "white" }),
                menuList: (base) => ({
                  ...base,
                  maxHeight: "240px",
                  scrollbarWidth: "thin",
                }),
                option: (base, state) => ({
                  ...base,
                  bg: state.isSelected
                    ? "primary.500"
                    : state.isFocused
                    ? "primary.100"
                    : "white",
                  color: state.isSelected
                    ? "white"
                    : "var(--chakra-colors-gray-800)",
                  _active: { bg: "primary.600", color: "white" },
                }),
              }}
              styles={{
                // style the portal container itself
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              }}
            />
          </Flex>

          {error && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="md"
              p={4}
            >
              <Text color="red.600">{error}</Text>
            </Box>
          )}

          {/* Documents Table */}
          {filteredDocuments.length === 0 ? (
            <Center py={12}>
              <VStack spacing={4}>
                <Text color="gray.600" fontSize="lg">
                  {documents.length === 0
                    ? "No documents found"
                    : "No documents match your search criteria"}
                </Text>
                {documents.length === 0 && (
                  <Button as={Link} to="/submit-document" colorScheme="primary">
                    Submit Your First Document
                  </Button>
                )}
              </VStack>
            </Center>
          ) : (
            <Box bg="white" borderRadius="md" overflow="hidden" shadow="sm">
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Document Type</Th>
                    <Th>Status</Th>
                    <Th>Created</Th>
                    <Th>Updated</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredDocuments.map((doc) => (
                    <Tr key={doc.id} _hover={{ bg: "gray.50" }}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="semibold" color="gray.800">
                            {getDocumentDisplayName(doc)}
                          </Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={2}>
                            {doc.prompt.length > 100
                              ? `${doc.prompt.substring(0, 100)}...`
                              : doc.prompt}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getStatusColor(doc.status)}
                          size="sm"
                        >
                          {doc.status.replace("_", " ")}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {new Date(doc.updated_at).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<Icon as={FiEye} />}
                            onClick={() => navigate(`/document/${doc.id}`)}
                            colorScheme="primary"
                          >
                            View
                          </Button>
                          {doc.status === "COMPLETED" && doc.output_path && (
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<Icon as={FiDownload} />}
                              onClick={() => downloadDocument(doc)}
                              colorScheme="primary"
                            >
                              Download
                            </Button>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      </Container>
      {/* Filename Dialog */}
      <FilenameDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDownload}
        defaultFilename={downloadData?.defaultFilename || ""}
        title="Download Document"
      />
    </Box>
  );
}
